import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronRight, Download, RefreshCw, X } from "lucide-react";
import { fmtDate, timeAgo } from "./UI";
import { ensureCallableAuthContext } from "../lib/crmCallableAuth";
import { callableErrorMessage } from "../lib/scanViewerApi";
import {
  canStreamToDisk,
  downloadErrorMessage,
  formatBytes,
  loadScanDownloadManifest,
  logDirectFileDownload,
  saveDownloadToDisk,
  startBrowserDownload,
} from "../lib/scanDownloadsApi";

/** Signed links last 15 minutes; refresh them while the panel stays open. */
const LINK_REFRESH_MS = 10 * 60 * 1000;
const RUNNING = new Set(["picking", "preparing", "downloading"]);

const BUNDLES = [
  {
    what: "everything",
    label: "Everything",
    hint: "Capture chunks and every file (text meshes compressed).",
  },
  {
    what: "captures",
    label: "Capture chunks",
    hint: "The uploaded chunk_N.zip files, as the phone sent them.",
  },
  {
    what: "keyframes",
    label: "Keyframes",
    hint: "Keyframe images (HEIC) and each chunk's manifest.json with per-frame pose and intrinsics.",
  },
];

function bundleSummary(what, bundles) {
  if (!bundles) return { text: "", empty: true };
  if (what === "keyframes") {
    const kf = bundles.keyframes || {};
    if (!kf.chunk_count) return { text: "No capture chunks", empty: true };
    if (!kf.frames) return { text: "No readable keyframes", empty: true };
    return {
      text: `${kf.frames} frames from ${kf.readable_chunks} chunk${kf.readable_chunks === 1 ? "" : "s"} · ${formatBytes(kf.bytes)}`,
      empty: false,
    };
  }
  const b = bundles[what] || {};
  if (!b.count) return { text: what === "captures" ? "No capture chunks" : "No files", empty: true };
  const noun = what === "captures" ? "chunk" : "file";
  return { text: `${b.count} ${noun}${b.count === 1 ? "" : "s"} · ${formatBytes(b.bytes)}`, empty: false };
}

function plural(n, one, many) {
  return `${n} ${n === 1 ? one : many}`;
}

/** Items in storage that a bundle could not read: they are listed under "skipped" in its manifest.json. */
function UnreadableNote({ count, started = false }) {
  if (!count) return null;
  return (
    <div className="scan-files-error">
      {plural(count, "item", "items")} could not be read and {started ? "will not be" : count === 1 ? "is not" : "are not"} in
      the zip; its manifest.json lists {count === 1 ? "it" : "them"} under "skipped".
    </div>
  );
}

function LeftEmptyNote({ job }) {
  if (!job.leftEmptyFile) return null;
  return (
    <div className="scan-files-muted">
      The browser created an empty {job.leftEmptyFile} where you chose to save it; delete it.
    </div>
  );
}

function JobStatus({ job, onCancel }) {
  if (!job || job.phase === "idle") return null;
  if (job.phase === "error") {
    return (
      <>
        <div className="scan-files-error">{job.message}</div>
        <LeftEmptyNote job={job} />
      </>
    );
  }
  if (job.phase === "cancelled") {
    return (
      <>
        <div className="scan-files-muted">Cancelled — nothing was saved.</div>
        <LeftEmptyNote job={job} />
      </>
    );
  }
  if (job.phase === "done") {
    return (
      <>
        <div className="scan-files-ok">
          Saved {job.filename} ({formatBytes(job.received)}).
        </div>
        <UnreadableNote count={job.unreadable} />
      </>
    );
  }
  if (job.phase === "started") {
    const plan = job.plan || {};
    return (
      <>
        <div className="scan-files-muted">
          Download started in your browser ({plan.entry_count ?? "?"} file{plan.entry_count === 1 ? "" : "s"},{" "}
          {formatBytes(plan.source_bytes)} of data) — its downloads list shows the progress.
        </div>
        <UnreadableNote count={(plan.skipped || []).filter((s) => s.kind === "unreadable").length} started />
      </>
    );
  }
  const percent =
    job.phase === "downloading" && job.expected && !job.compressed
      ? Math.min(99, Math.floor((job.received / job.expected) * 100))
      : null;
  const label =
    job.phase === "picking"
      ? "Choose where to save… (an existing file you pick is emptied right away)"
      : job.phase === "preparing"
        ? "Preparing the zip on the server…"
        : percent != null
          ? `${formatBytes(job.received)} of ${formatBytes(job.expected)} (${percent}%)`
          : `${formatBytes(job.received)} received${job.compressed ? " (compressing text meshes)" : ""}`;
  return (
    <div>
      <div className="scan-files-progress-row">
        <span>{label}</span>
        {job.phase !== "picking" ? (
          <button type="button" className="btn btn-sm scan-files-icon-btn" onClick={onCancel} title="Cancel download">
            <X size={12} /> Cancel
          </button>
        ) : null}
      </div>
      {job.phase !== "picking" ? (
        <div className="progress-bar">
          <div
            className={`progress-fill${percent == null ? " scan-files-progress-indeterminate" : ""}`}
            style={{ width: percent == null ? "35%" : `${percent}%` }}
          />
        </div>
      ) : null}
    </div>
  );
}

/** Where the file sits in the scan's folder: "captures/", "files/", "models/" … (or the full path when outside). */
function folderLabel(row) {
  if (row.status === "outside_scan") return row.rel_path;
  const slash = String(row.rel_path || "").lastIndexOf("/");
  return slash > 0 ? `${row.rel_path.slice(0, slash)}/` : row.rel_path;
}

function rowStatusNote(row) {
  if (row.status === "missing") return "Not in storage";
  if (row.status === "outside_scan") return "Outside this scan's folder";
  return "";
}

/**
 * "Files & downloads" for one scan in the viewer modal: every file in the
 * scan's storage folder (capture chunks, models, PLYs, photos) with per-file
 * downloads, plus three zip bundles. Collapsed by default; it lists (and logs)
 * nothing until opened. `onBusyChange(busy)`: a download streamed by this page
 * is running (closing the viewer would cancel it).
 */
export default function ScanFilesPanel({ scanId, onBusyChange }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [jobs, setJobs] = useState({});
  const controllers = useRef({});
  const requested = useRef(false);

  // refresh: renewing the links (timer, Refresh, an expired link) — not logged as a panel open.
  const load = useCallback(async ({ refresh = false } = {}) => {
    setLoading(true);
    setError("");
    try {
      const auth = await ensureCallableAuthContext();
      if (!auth.ok) {
        setError(auth.message);
        return null;
      }
      const next = await loadScanDownloadManifest(scanId, { refresh });
      if (!next || !Array.isArray(next.files)) throw new Error("The server returned no file list.");
      setData(next);
      return next;
    } catch (e) {
      setError(callableErrorMessage(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, [scanId]);

  useEffect(() => {
    requested.current = false;
    setData(null);
    setJobs({});
    setError("");
  }, [scanId]);

  // First open lists the files (and issues links); after that only Refresh / Retry do.
  useEffect(() => {
    if (!open || requested.current) return;
    requested.current = true;
    load();
  }, [open, load]);

  useEffect(() => {
    if (!open || !data?.urls_expire_at) return undefined;
    const timer = setInterval(() => load({ refresh: true }), LINK_REFRESH_MS);
    return () => clearInterval(timer);
  }, [open, data?.urls_expire_at, load]);

  // Closing the viewer cancels downloads streamed by this page (no partial files are kept).
  useEffect(() => {
    const running = controllers.current;
    return () => Object.values(running).forEach((controller) => controller.abort());
  }, []);

  const updateJob = (key, patch) =>
    setJobs((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), ...patch } }));

  async function runDownload(key, { what, id, suggestedName }) {
    if (RUNNING.has(jobs[key]?.phase)) return;
    if (canStreamToDisk()) {
      const controller = new AbortController();
      controllers.current[key] = controller;
      updateJob(key, {
        phase: "picking",
        streamed: true,
        message: "",
        received: 0,
        expected: null,
        unreadable: null,
        leftEmptyFile: null,
      });
      try {
        const result = await saveDownloadToDisk({
          scanId,
          what,
          id,
          suggestedName,
          signal: controller.signal,
          onProgress: (progress) => updateJob(key, progress),
        });
        if (result.cancelled) updateJob(key, { phase: "idle" });
        else {
          updateJob(key, {
            phase: "done",
            received: result.bytes,
            filename: result.filename,
            unreadable: result.unreadable,
          });
        }
      } catch (e) {
        const leftEmptyFile = e?.leftEmptyFile || null;
        if (e?.name === "AbortError") updateJob(key, { phase: "cancelled", leftEmptyFile });
        else updateJob(key, { phase: "error", message: downloadErrorMessage(e), leftEmptyFile });
      } finally {
        delete controllers.current[key];
      }
      return;
    }
    updateJob(key, { phase: "preparing", streamed: false, message: "", leftEmptyFile: null });
    try {
      const plan = await startBrowserDownload({
        scanId,
        what,
        id,
        // The server refused the download itself (after the size check passed).
        onError: (message) => updateJob(key, { phase: "error", message: `Download failed: ${message}` }),
      });
      updateJob(key, { phase: "started", plan });
    } catch (e) {
      updateJob(key, { phase: "error", message: downloadErrorMessage(e) });
    }
  }

  function cancel(key) {
    controllers.current[key]?.abort();
  }

  async function downloadFile(row, event) {
    const expiresAt = Date.parse(data?.urls_expire_at || "") || 0;
    if (row.url && Date.now() < expiresAt - 30 * 1000) {
      logDirectFileDownload(scanId, row); // the link downloads by itself; this only records it
      return;
    }
    event.preventDefault();
    if (row.url) {
      const fresh = await load({ refresh: true });
      const again = fresh?.files?.find((f) => f.id === row.id);
      if (again?.url) {
        logDirectFileDownload(scanId, again);
        window.location.assign(again.url);
        return;
      }
    }
    // No signed link (signing unavailable): stream it through the CRM instead.
    await runDownload(`file:${row.id}`, { what: "file", id: row.id, suggestedName: row.download_name });
  }

  const files = data?.files || [];
  const servable = files.filter((f) => f.status === "ok");
  const notServed = files.length - servable.length;
  // Only downloads this page streams to disk stop when the viewer closes (a
  // browser-managed download keeps going).
  const anyStreaming = Object.values(jobs).some(
    (job) => job.streamed && (job.phase === "downloading" || job.phase === "preparing")
  );

  useEffect(() => {
    onBusyChange?.(anyStreaming);
  }, [anyStreaming, onBusyChange]);

  return (
    <div className="scan-files-panel">
      <button
        type="button"
        className="scan-files-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <span>Files &amp; downloads</span>
        {data ? (
          <span className="scan-files-muted">
            {data.totals?.everything?.count ?? servable.length} files · {formatBytes(data.totals?.everything?.bytes)}
          </span>
        ) : null}
      </button>

      {open ? (
        <div className="scan-files-body">
          {loading && !data ? <div className="scan-files-muted">Listing the scan's files…</div> : null}
          {error ? (
            <div className="scan-files-error">
              Could not list files: {error}{" "}
              <button type="button" className="btn btn-sm" onClick={() => load({ refresh: Boolean(data) })}>
                Retry
              </button>
            </div>
          ) : null}

          {data ? (
            <>
              <div className="scan-files-bundles">
                {BUNDLES.map((bundle) => {
                  const summary = bundleSummary(bundle.what, data.bundles);
                  const job = jobs[bundle.what];
                  const running = RUNNING.has(job?.phase);
                  return (
                    <div key={bundle.what} className="scan-files-bundle">
                      <div className="scan-files-bundle-head">
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 500 }}>{bundle.label}</div>
                          <div className="scan-files-muted">{summary.text}</div>
                        </div>
                        <button
                          type="button"
                          className="btn btn-sm btn-primary scan-files-icon-btn"
                          disabled={summary.empty || running}
                          title={bundle.hint}
                          onClick={() =>
                            runDownload(bundle.what, {
                              what: bundle.what,
                              suggestedName: `${scanId}_${bundle.what}.zip`,
                            })
                          }
                        >
                          <Download size={12} /> {running ? "Working…" : "Zip"}
                        </button>
                      </div>
                      <div className="scan-files-muted" style={{ marginTop: 4 }}>
                        {bundle.hint}
                      </div>
                      {bundle.what === "keyframes" && data.bundles?.keyframes?.unreadable?.length ? (
                        <div className="scan-files-muted" style={{ marginTop: 4 }}>
                          {data.bundles.keyframes.unreadable.length} chunk
                          {data.bundles.keyframes.unreadable.length === 1 ? "" : "s"} unreadable (
                          {data.bundles.keyframes.unreadable.map((u) => u.source).join(", ")}).
                          {data.bundles.keyframes.unreadable.some((u) => u.storage_error)
                            ? " Storage did not answer for some of them: Refresh, then try again."
                            : ""}
                        </div>
                      ) : null}
                      <JobStatus job={job} onCancel={() => cancel(bundle.what)} />
                    </div>
                  );
                })}
              </div>

              {anyStreaming ? (
                <div className="scan-files-muted" style={{ marginTop: 8 }}>
                  Keep this viewer open until the download finishes; closing it cancels the download.
                </div>
              ) : null}
              {data.sign_error ? (
                <div className="scan-files-muted" style={{ marginTop: 8 }}>
                  Direct links are unavailable ({data.sign_error}); single files download through the CRM instead.
                </div>
              ) : null}
              {data.api_error ? (
                <div className="scan-files-muted" style={{ marginTop: 8 }}>
                  Backend API unreachable; file details come from Firestore and storage only.
                </div>
              ) : null}

              <div className="scan-files-table-head">
                <span>
                  {servable.length} file{servable.length === 1 ? "" : "s"} in storage
                  {notServed ? ` · ${notServed} recorded file${notServed === 1 ? "" : "s"} not served` : ""}
                </span>
                <button
                  type="button"
                  className="btn btn-sm scan-files-icon-btn"
                  onClick={() => load({ refresh: true })}
                  disabled={loading}
                  title="Reload the list and the download links"
                >
                  <RefreshCw size={12} /> {loading ? "Refreshing…" : "Refresh"}
                </button>
              </div>
              <div className="table-wrap scan-files-table-wrap">
                <table className="scan-files-table">
                  <thead>
                    <tr>
                      <th>File</th>
                      <th>Role / tags</th>
                      <th style={{ textAlign: "right" }}>Size</th>
                      <th>Uploaded</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {files.map((row) => {
                      const job = jobs[`file:${row.id}`];
                      const note = rowStatusNote(row);
                      return (
                        <tr key={row.id} className={row.status === "ok" ? "" : "scan-files-row-muted"}>
                          <td>
                            <div className="scan-files-name">{row.filename || row.rel_path}</div>
                            <div className="scan-files-muted mono scan-files-path">{folderLabel(row)}</div>
                            {row.note && row.status === "ok" ? (
                              <div className="scan-files-muted">{row.note}</div>
                            ) : null}
                            <JobStatus job={job} onCancel={() => cancel(`file:${row.id}`)} />
                          </td>
                          <td className="scan-files-role">
                            <div>{row.role || "—"}</div>
                            {row.tags?.length ? <div className="scan-files-muted">{row.tags.join(", ")}</div> : null}
                            {row.record_count > 1 ? (
                              <div className="scan-files-muted">{row.record_count} uploads (newest kept)</div>
                            ) : null}
                          </td>
                          <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>{formatBytes(row.size)}</td>
                          <td style={{ whiteSpace: "nowrap" }} title={row.uploaded_at || row.created_at || ""}>
                            {row.uploaded_at || row.created_at ? (
                              <>
                                {fmtDate(row.uploaded_at || row.created_at)}
                                <div className="scan-files-muted">{timeAgo(row.uploaded_at || row.created_at)}</div>
                              </>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td style={{ whiteSpace: "nowrap" }}>
                            {row.status !== "ok" ? (
                              <span className="scan-files-muted">{note}</span>
                            ) : row.url ? (
                              <a
                                className="btn btn-sm scan-files-icon-btn"
                                href={row.url}
                                onClick={(e) => downloadFile(row, e)}
                                title={`Download as ${row.download_name}`}
                                aria-label={`Download ${row.filename}`}
                              >
                                <Download size={13} />
                              </a>
                            ) : (
                              <button
                                type="button"
                                className="btn btn-sm scan-files-icon-btn"
                                disabled={RUNNING.has(job?.phase)}
                                onClick={(e) => downloadFile(row, e)}
                                title={`Download as ${row.download_name}`}
                                aria-label={`Download ${row.filename}`}
                              >
                                <Download size={13} />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {data.recent_downloads?.length ? (
                <div className="scan-files-recent">
                  <div className="scan-files-muted" style={{ marginBottom: 4 }}>
                    Recent downloads of this scan
                  </div>
                  {data.recent_downloads.map((entry) => (
                    <div key={entry.id} className="scan-files-muted">
                      {entry.email || entry.uid || "someone"} · {entry.what}
                      {entry.filename ? ` (${entry.filename})` : ""}
                      {entry.via === "signed_link"
                        ? " · direct link"
                        : entry.status && entry.status !== "completed"
                          ? ` · ${entry.status}`
                          : ""}
                      {entry.unreadable_count ? ` · ${entry.unreadable_count} unreadable, not in the zip` : ""}
                      {entry.created_at ? ` · ${timeAgo(entry.created_at)}` : ""}
                    </div>
                  ))}
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
